import { Box, Paper } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  ehSemPermissao,
  estruturacaoConsultorService,
} from '@/services/estruturacaoConsultorService'
import { produtoService } from '@/services/produtoService'
import { ChecklistReserva, EstadoChecklist, ReservaEstruturacao } from '@/types/estruturacao'
import { HeaderResumoEstruturacao } from '@/components/estruturacao/HeaderResumoEstruturacao'
import { EtapaDiagnostico } from '@/components/estruturacao/EtapaDiagnostico'
import {
  EtapaFormularioProduto,
  SEM_FORMULARIO_ESTRUTURACAO,
} from '@/components/estruturacao/EtapaFormularioProduto'
import { useFormularioReserva } from '@/hooks/useFormularioReserva'
import { EtapaDocumentos } from '@/components/estruturacao/EtapaDocumentos'
import { EtapaDefesa } from '@/components/estruturacao/EtapaDefesa'
import { EtapaParceiro } from '@/components/estruturacao/EtapaParceiro'
import {
  EtapaNavegavel,
  NavegacaoEtapas,
  RodapeNavegacaoEtapas,
} from '@/components/estruturacao/NavegacaoEtapas'

/**
 * As 5 etapas do workspace, na ordem do Figma (12:2 → 12:182). A etapa 2 era o bloco fixo
 * "Dados complementares" (V39); desde L6 é o formulário ESTRUTURACAO do produto ("Dados do
 * produto") — a POSIÇÃO não muda: `?etapa=3` continua sendo Documentos (deep links).
 */
export const ETAPAS_ESTRUTURACAO = [
  'Diagnóstico',
  'Dados do produto',
  'Documentos',
  'Defesa de crédito',
  'Parceiro',
] as const

/** `?etapa=` fora de 1..5 (ou ausente) cai na etapa 1 — URL é entrada de usuário. */
const etapaDaUrl = (raw: string | null): number => {
  const n = Number(raw)
  return Number.isInteger(n) && n >= 1 && n <= ETAPAS_ESTRUTURACAO.length ? n : 1
}

/**
 * Workspace de estruturação por reserva (Onda 4 / A09) — CONSULTOR e back-office.
 *
 * Header-resumo (Oportunidade · Produto · Status · Documentação %) + stepper NÃO linear
 * de 5 etapas, com a etapa atual na URL (`?etapa=`) para link direto e navegação livre.
 * O escopo é do backend: reserva de outro consultor volta como "não encontrada"
 * (fail-closed) e a tela mostra o estado de sem permissão.
 */
export const EstruturacaoReservaPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const etapa = etapaDaUrl(searchParams.get('etapa'))
  // Deep link da Central (L7 / Q40): `?etapa=3&item=<chave>` rola até o item do checklist.
  const itemDestacado = searchParams.get('item')

  const [reserva, setReserva] = useState<ReservaEstruturacao | null>(null)
  const [checklist, setChecklist] = useState<ChecklistReserva | null>(null)
  /** Estado do GET /checklist, separado da reserva: falhar aqui não derruba o workspace. */
  const [checklistEstado, setChecklistEstado] = useState<EstadoChecklist>('carregando')
  const [produtoNome, setProdutoNome] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [falha, setFalha] = useState<'erro' | 'semPermissao' | null>(null)
  /** Formulário ESTRUTURACAO: a etapa 2 o edita; o stepper desabilita o passo sem ele. */
  const formProduto = useFormularioReserva(reserva?.id ?? null, 'ESTRUTURACAO')
  const semFormularioProduto = formProduto.estado === 'ok' && !formProduto.temFormulario

  /** Busca o checklist com estado explícito (carregando → ok | erro) — nunca "null eterno". */
  const carregarChecklist = useCallback(async () => {
    if (!id) return
    setChecklistEstado('carregando')
    try {
      setChecklist(await estruturacaoConsultorService.checklist(id))
      setChecklistEstado('ok')
    } catch {
      setChecklistEstado('erro')
    }
  }, [id])

  const carregar = useCallback(async () => {
    if (!id) return
    setCarregando(true)
    setFalha(null)
    try {
      const r = await estruturacaoConsultorService.reserva(id)
      setReserva(r)
      // Checklist e nome do produto são complementos: falharem não derruba o workspace.
      void carregarChecklist()
      if (r.produtoId) {
        produtoService
          .buscar(r.produtoId)
          .then((p) => setProdutoNome(p.nome ?? null))
          .catch(() => setProdutoNome(null))
      }
    } catch (e) {
      setFalha(ehSemPermissao(e) ? 'semPermissao' : 'erro')
    } finally {
      setCarregando(false)
    }
  }, [id, carregarChecklist])

  useEffect(() => {
    void carregar()
  }, [carregar])

  /** Após upload/envio: atualiza reserva + checklist sem resetar a etapa atual. */
  const recarregar = useCallback(() => {
    if (!id) return
    estruturacaoConsultorService.reserva(id).then(setReserva).catch(() => undefined)
    void carregarChecklist()
  }, [id, carregarChecklist])

  const irParaEtapa = (n: number) => {
    setSearchParams({ etapa: String(n) }, { replace: true })
  }

  /** Trilha de etapas: o rodapé rola de volta até ela se o usuário desceu a página. */
  const trilhaRef = useRef<HTMLDivElement>(null)
  const irParaEtapaPeloRodape = (n: number) => {
    irParaEtapa(n)
    const trilha = trilhaRef.current
    if (
      trilha &&
      trilha.getBoundingClientRect().top < 0 &&
      typeof trilha.scrollIntoView === 'function'
    ) {
      trilha.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  if (carregando) return <EstadoDeLista estado="carregando" />
  if (falha === 'semPermissao') {
    return (
      <EstadoDeLista
        estado="semPermissao"
        detalhe="Esta reserva pode pertencer a outro consultor — cada um vê apenas a própria carteira."
      />
    )
  }
  if (falha === 'erro' || !reserva) {
    return (
      <EstadoDeLista
        estado="erro"
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
      />
    )
  }

  const numero =
    reserva.numero != null ? `RES-${String(reserva.numero).padStart(4, '0')}` : undefined

  // Documentos: legenda com o "N de M enviados" do mesmo checklist do header (sem regra nova).
  const docs = checklistEstado === 'ok' && checklist && checklist.total > 0 ? checklist : null
  const etapas: EtapaNavegavel[] = ETAPAS_ESTRUTURACAO.map((rotulo, i) => {
    // Sem formulário de estruturação no produto, o passo 2 fica desabilitado (e a
    // numeração dos demais NÃO muda).
    if (i === 1 && semFormularioProduto) {
      return {
        rotulo,
        desabilitada: true,
        legenda: 'Sem dados nesta etapa',
        legendaAcessivel: SEM_FORMULARIO_ESTRUTURACAO,
      }
    }
    if (i === 2 && docs) {
      const completos = docs.percentual >= 100
      const texto = `${docs.presentes} de ${docs.total} enviados`
      return {
        rotulo,
        concluida: completos,
        legendaAcessivel: texto,
        legenda: completos ? (
          <>
            <CheckIcon sx={{ fontSize: 14 }} aria-hidden />
            {texto}
          </>
        ) : (
          texto
        ),
      }
    }
    return { rotulo }
  })
  /** Anterior/Próxima pulam passos desabilitados, como a trilha (que não os deixa clicar). */
  const vizinha = (passo: 1 | -1): number | null => {
    for (let n = etapa + passo; n >= 1 && n <= etapas.length; n += passo) {
      if (!etapas[n - 1].desabilitada) return n
    }
    return null
  }
  const anterior = vizinha(-1)
  const proxima = vizinha(1)

  return (
    <Box>
      <PageHeader title="Estruturação da Reserva" subtitle={numero} />

      <HeaderResumoEstruturacao
        reserva={reserva}
        produtoNome={produtoNome}
        checklist={checklist}
        checklistEstado={checklistEstado}
      />

      <Box ref={trilhaRef} sx={{ scrollMarginTop: 16 }}>
        <NavegacaoEtapas etapas={etapas} atual={etapa} onIrPara={irParaEtapa} />
      </Box>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        {etapa === 1 && (
          <EtapaDiagnostico reservaId={reserva.id} produtoId={reserva.produtoId ?? null} />
        )}
        {etapa === 2 && <EtapaFormularioProduto form={formProduto} />}
        {etapa === 3 && (
          <EtapaDocumentos
            reserva={reserva}
            checklist={checklist}
            checklistEstado={checklistEstado}
            onRecarregar={recarregar}
            onRecarregarChecklist={() => void carregarChecklist()}
            itemDestacado={itemDestacado}
          />
        )}
        {etapa === 4 && <EtapaDefesa reservaId={reserva.id} />}
        {etapa === 5 && <EtapaParceiro reserva={reserva} />}

        <RodapeNavegacaoEtapas
          anterior={anterior}
          proxima={proxima}
          onIrPara={irParaEtapaPeloRodape}
        />
      </Paper>
    </Box>
  )
}

export default EstruturacaoReservaPage
